import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  X,
  Mail,
  Building,
  Briefcase,
  Phone,
  Tag,
  Clock,
  MessageCircle,
  MessageSquare,
  Send,
  ChevronRight,
  Loader2,
  Edit2,
  Check,
  Plus,
  User,
  Star,
  Copy,
  ExternalLink,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { contactsApi, UpdateContactRequest } from '@/services/contactsApi'
import { tagsApi } from '@/services/tagsApi'
import { Input } from '@/components/ui/input'

interface ContactPanelProps {
  contactId: string
  detectedName?: string
  isOpen: boolean
  onClose: () => void
}

export function ContactPanel({ contactId, detectedName, isOpen, onClose }: ContactPanelProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [editedFields, setEditedFields] = useState<UpdateContactRequest>({})
  const [showTimeline, setShowTimeline] = useState(false)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const copyToClipboard = (text: string, key: string) => {
    if (!text) return
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {})
    }
    setCopiedKey(key)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  // Fetch contact details
  const { data: contact, isLoading: isLoadingContact, isError } = useQuery({
    queryKey: ['contact', contactId],
    queryFn: () => contactsApi.getContactById(contactId),
    enabled: !!contactId && isOpen,
    retry: false,
  })

  // Create contact mutation
  // NOTE (B4 fix): contactId here is conversation.waContact (the phone number),
  // NOT the conversation UUID — this is correct because ContactPanel receives
  // contactId={conversation.waContact} from ConversationView.
  const createContactMutation = useMutation({
    mutationFn: (data: { waContact: string; name?: string; source?: string }) =>
      contactsApi.createContact({ ...data, source: 'whatsapp' }),
    onSuccess: (newContact) => {
      queryClient.setQueryData(['contact', contactId], newContact)
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })

  // Fetch contact timeline
  const { data: timeline, isLoading: isLoadingTimeline } = useQuery({
    queryKey: ['contactTimeline', contactId],
    queryFn: () => contactsApi.getContactTimeline(contactId),
    enabled: !!contactId && isOpen && showTimeline,
  })

  // Fetch all tags
  const { data: allTags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.getAll(),
    enabled: isOpen,
  })

  // Update contact mutation
  const updateContactMutation = useMutation({
    mutationFn: (data: UpdateContactRequest) => contactsApi.updateContact(contactId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', contactId] })
      setIsEditing(false)
      setEditedFields({})
    },
  })

  // Add tag to contact
  const addTagMutation = useMutation({
    mutationFn: (tagId: string) => contactsApi.addTagToContact(contactId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', contactId] })
    },
  })

  // Remove tag from contact
  const removeTagMutation = useMutation({
    mutationFn: (tagId: string) => contactsApi.removeTagFromContact(contactId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', contactId] })
    },
  })

  // Toggle favorite
  const toggleFavoriteMutation = useMutation({
    mutationFn: () => contactsApi.toggleFavorite(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', contactId] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })

  // Update status
  const updateStatusMutation = useMutation({
    mutationFn: (status: 'active' | 'inactive' | 'blocked') => contactsApi.updateStatus(contactId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', contactId] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false)
      setEditedFields({})
      setShowTimeline(false)
    }
  }, [isOpen])

  const handleSaveEdit = () => {
    if (Object.keys(editedFields).length > 0) {
      updateContactMutation.mutate(editedFields)
    } else {
      setIsEditing(false)
    }
  }

  const handleFieldChange = (field: keyof UpdateContactRequest, value: string) => {
    setEditedFields(prev => ({ ...prev, [field]: value }))
  }

  const handleAddTag = (tagId: string) => {
    addTagMutation.mutate(tagId)
  }

  const handleRemoveTag = (tagId: string) => {
    removeTagMutation.mutate(tagId)
  }

  // Get available tags (not assigned to contact)
  const availableTags = contact
    ? allTags.filter((tag) => !contact.tags.some((t) => t.id === tag.id))
    : []

  const getChannelLabel = (source?: string) => {
    switch (source) {
      case 'whatsapp': return 'WhatsApp'
      case 'telegram': return 'Telegram'
      case 'webchat': return 'WebChat'
      case 'manual': return 'Manual'
      default: return source || 'WhatsApp'
    }
  }

  const formatDate = (date: Date | string | number | null | undefined) => {
    if (!date) return 'N/A'
    const parsed = date instanceof Date ? date : new Date(date)
    if (isNaN(parsed.getTime())) return 'N/A'
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(parsed)
  }

  const getTimelineIcon = (type: string) => {
    switch (type) {
      case 'message': return <MessageCircle className="w-4 h-4" />
      case 'note': return <Edit2 className="w-4 h-4" />
      case 'tag_added': return <Tag className="w-4 h-4" />
      case 'tag_removed': return <Tag className="w-4 h-4" />
      case 'mode_changed': return <Send className="w-4 h-4" />
      case 'conversation_status': return <Clock className="w-4 h-4" />
      default: return <Clock className="w-4 h-4" />
    }
  }

  const getTimelineLabel = (type: string, data: Record<string, unknown>) => {
    switch (type) {
      case 'message': return data.direction === 'out' ? 'Mensaje enviado' : 'Mensaje recibido'
      case 'note': return 'Nota interna'
      case 'tag_added': return `Etiqueta "${data.tagName}" asignada`
      case 'tag_removed': return `Etiqueta "${data.tagName}" removida`
      case 'mode_changed': return `Cambió a modo ${data.newMode}`
      case 'conversation_status': return `Conversación ${data.status}`
      default: return type
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/30 z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-label="Perfil del contacto"
        aria-modal="true"
        className={`fixed right-0 top-0 h-full w-full sm:w-[360px] bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-slate-100 flex-shrink-0">
          <h2 className="font-bold text-slate-800">Perfil del contacto</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoadingContact ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#075f55]" />
          </div>
        ) : isError ? (
          // Show create contact option when contact not found
          <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <MessageCircle className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Contacto no encontrado</h3>
            <p className="text-sm text-gray-500 mb-4">
              Este contacto no existe en la base de datos. ¿Deseas crearlo?
            </p>
            <button
              onClick={() => createContactMutation.mutate({ waContact: contactId, name: detectedName })}
              disabled={createContactMutation.isPending}
              className="bg-[#075f55] hover:bg-[#064e46] text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              {createContactMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span>Crear Contacto {detectedName ? `(${detectedName})` : ''}</span>
                </>
              )}
            </button>
            {createContactMutation.isError && (
              <p className="text-sm text-red-500 mt-2">
                {(createContactMutation.error as any)?.response?.data?.error ?? 'Error al crear contacto. Intenta nuevamente.'}
              </p>
            )}
          </div>
        ) : contact && !isError ? (
          <>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Avatar & Name — centered, 96px */}
              <div className="p-6 flex flex-col items-center border-b border-slate-100">
                <div className="relative mb-4">
                  <div className="w-24 h-24 rounded-full bg-[#075f55]/20 flex items-center justify-center text-[#075f55] text-2xl font-black border-4 border-white shadow-sm">
                    {(contact.name || contact.waContact)?.slice(0, 2).toUpperCase() || '??'}
                  </div>
                  <div className="absolute -bottom-1 -right-1 bg-white p-1 rounded-full shadow-sm">
                    <div className="w-5 h-5 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                      <MessageCircle className="w-3 h-3 text-[#25D366]" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-black text-gray-900 text-center">
                    {contact.name || contact.waContact}
                  </h3>
                  <button
                    onClick={() => toggleFavoriteMutation.mutate()}
                    disabled={toggleFavoriteMutation.isPending}
                    className="p-1.5 focus:outline-none transition-colors"
                    title={contact.isFavorite ? "Quitar de favoritos" : "Añadir a favoritos"}
                  >
                    <Star 
                      className={`w-5 h-5 transition-colors ${contact.isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 hover:text-yellow-400'}`} 
                    />
                  </button>
                </div>

                {/* Banner de Sincronización 1-Clic de Nombre Detectado */}
                {detectedName && contact.name !== detectedName && (
                  <div className="mt-3 w-full rounded-xl border border-emerald-200 bg-emerald-50/70 p-2.5 text-center shadow-2xs">
                    <p className="text-[11px] text-emerald-900 font-medium">
                      IA detectó: <strong className="font-bold">{detectedName}</strong>
                    </p>
                    <button
                      type="button"
                      onClick={() => updateContactMutation.mutate({ name: detectedName })}
                      disabled={updateContactMutation.isPending}
                      className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-[#075f55] px-2.5 py-1 text-xs font-bold text-white shadow-2xs hover:bg-[#064e46] active:scale-95 transition-all disabled:opacity-50"
                    >
                      <Check className="h-3 w-3" />
                      <span>{updateContactMutation.isPending ? 'Actualizando...' : 'Guardar como nombre de contacto'}</span>
                    </button>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex items-center gap-1.5 text-emerald-600 font-medium text-xs bg-emerald-50 px-3 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {getChannelLabel(contact.source)}
                  </div>
                  <select
                    value={contact.status || 'active'}
                    onChange={(e) => updateStatusMutation.mutate(e.target.value as 'active' | 'inactive' | 'blocked')}
                    disabled={updateStatusMutation.isPending}
                    className={`text-xs font-medium px-2 py-1 rounded-full border-0 outline-none cursor-pointer appearance-none ${
                       contact.status === 'blocked' ? 'bg-red-50 text-red-600' :
                       contact.status === 'inactive' ? 'bg-gray-100 text-gray-600' :
                       'bg-blue-50 text-blue-600'
                    }`}
                  >
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                    <option value="blocked">Bloqueado</option>
                  </select>
                </div>

                {/* Quick Action Bar */}
                <div className="grid grid-cols-3 gap-2 w-full mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      const convId = contact.recentConversations?.[0]?.id
                      if (convId) {
                        navigate(`/app/conversations/${convId}`)
                      } else {
                        navigate(`/app/conversations?contact=${encodeURIComponent(contact.waContact)}`)
                      }
                      onClose()
                    }}
                    className="flex flex-col items-center justify-center py-2 px-1 rounded-xl bg-[#075f55]/10 hover:bg-[#075f55]/20 text-[#075f55] transition-colors text-xs font-semibold"
                    title="Ir a conversación"
                  >
                    <MessageSquare className="w-4 h-4 mb-1" />
                    <span>Chat</span>
                  </button>

                  <a
                    href={`tel:${contact.phone || contact.waContact}`}
                    className="flex flex-col items-center justify-center py-2 px-1 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors text-xs font-semibold text-center"
                    title="Llamar"
                  >
                    <Phone className="w-4 h-4 mb-1" />
                    <span>Llamar</span>
                  </a>

                  <button
                    type="button"
                    onClick={() => copyToClipboard(contact.phone || contact.waContact, 'top-phone')}
                    className="flex flex-col items-center justify-center py-2 px-1 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors text-xs font-semibold"
                    title="Copiar teléfono"
                  >
                    {copiedKey === 'top-phone' ? (
                      <>
                        <Check className="w-4 h-4 mb-1 text-emerald-600" />
                        <span className="text-emerald-600">¡Copiado!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mb-1" />
                        <span>Copiar</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Editable Fields */}
              <div className="p-4 border-b space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Información</h4>
                  {!isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="text-xs text-[#075f55] hover:text-[#064e46] flex items-center gap-1"
                    >
                      <Edit2 className="w-3 h-3" />
                      Editar
                    </button>
                  ) : (
                    <button
                      onClick={handleSaveEdit}
                      disabled={updateContactMutation.isPending}
                      className="text-xs text-[#075f55] hover:text-[#064e46] flex items-center gap-1 disabled:opacity-50"
                    >
                      {updateContactMutation.isPending ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Check className="w-3 h-3" />
                      )}
                      Guardar
                    </button>
                  )}
                </div>

                {/* Name */}
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  {isEditing ? (
                    <Input
                      value={editedFields.name ?? contact.name ?? ''}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      placeholder="Nombre"
                      className="flex-1 h-8 text-sm"
                    />
                  ) : (
                    <span className="text-sm text-gray-600 truncate">
                      {contact.name || 'No definido'}
                    </span>
                  )}
                </div>

                {/* Email */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    {isEditing ? (
                      <Input
                        type="email"
                        value={editedFields.email ?? contact.email ?? ''}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        placeholder="Email"
                        className="flex-1 h-8 text-sm"
                      />
                    ) : (
                      <span className="text-sm text-gray-600 truncate">
                        {contact.email || 'No definido'}
                      </span>
                    )}
                  </div>
                  {!isEditing && contact.email && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(contact.email!, 'field-email')}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors flex-shrink-0"
                      title="Copiar email"
                    >
                      {copiedKey === 'field-email' ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                  )}
                </div>

                {/* Phone */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    {isEditing ? (
                      <Input
                        type="tel"
                        value={editedFields.phone ?? contact.phone ?? ''}
                        onChange={(e) => handleFieldChange('phone', e.target.value)}
                        placeholder="Teléfono"
                        className="flex-1 h-8 text-sm"
                      />
                    ) : (
                      <span className="text-sm text-gray-600 truncate">
                        {contact.phone || contact.waContact || 'No definido'}
                      </span>
                    )}
                  </div>
                  {!isEditing && (contact.phone || contact.waContact) && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(contact.phone || contact.waContact, 'field-phone')}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors flex-shrink-0"
                      title="Copiar teléfono"
                    >
                      {copiedKey === 'field-phone' ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                  )}
                </div>

                {/* Company */}
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  {isEditing ? (
                    <Input
                      value={editedFields.company ?? contact.company ?? ''}
                      onChange={(e) => handleFieldChange('company', e.target.value)}
                      placeholder="Empresa"
                      className="flex-1 h-8 text-sm"
                    />
                  ) : (
                    <span className="text-sm text-gray-600 truncate">
                      {contact.company || 'No definido'}
                    </span>
                  )}
                </div>

                {/* Position */}
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  {isEditing ? (
                    <Input
                      value={editedFields.position ?? contact.position ?? ''}
                      onChange={(e) => handleFieldChange('position', e.target.value)}
                      placeholder="Cargo"
                      className="flex-1 h-8 text-sm"
                    />
                  ) : (
                    <span className="text-sm text-gray-600 truncate">
                      {contact.position || 'No definido'}
                    </span>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div className="p-4 border-b">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Etiquetas</h4>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {contact.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: tag.color + '20', color: tag.color }}
                    >
                      {tag.name}
                      <button
                        onClick={() => handleRemoveTag(tag.id)}
                        className="hover:bg-black/10 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {availableTags.length > 0 && (
                    <div className="relative group">
                      <button className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
                        <Plus className="w-3 h-3" />
                        Agregar
                      </button>
                      {/* Dropdown */}
                      <div className="absolute left-0 top-full mt-1 z-10 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-40 hidden group-hover:block max-h-40 overflow-y-auto">
                        {availableTags.map((tag) => (
                          <button
                            key={tag.id}
                            onClick={() => handleAddTag(tag.id)}
                            className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 text-left text-xs"
                          >
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: tag.color }}
                            />
                            {tag.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats — 2-col grid */}
              <div className="p-6 bg-slate-50 border-t border-slate-100 grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Total Conversaciones</p>
                  <p className="text-xl font-black text-[#075f55]">{contact.totalConversations}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Último contacto</p>
                  <p className="text-xs font-bold text-gray-800 mt-1">{formatDate(contact.lastContactAt)}</p>
                </div>
              </div>

              {/* Custom Fields */}
              {contact.customFields && Object.keys(contact.customFields).length > 0 && (
                <div className="p-4 border-b">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Campos personalizados</h4>
                  <div className="space-y-2">
                    {Object.entries(contact.customFields).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{key}</span>
                        <span className="font-medium text-gray-900">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Conversations */}
              <div className="p-4 border-b">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Conversaciones Recientes ({contact.recentConversations?.length ?? 0})
                  </h4>
                </div>
                {contact.recentConversations && contact.recentConversations.length > 0 ? (
                  <div className="space-y-2">
                    {contact.recentConversations.map((conv) => {
                      const isClosed = conv.status === 'closed'
                      const isPending = conv.status === 'pending'
                      const badgeColor = isClosed
                        ? 'bg-gray-100 text-gray-600'
                        : isPending
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-emerald-100 text-emerald-700'

                      return (
                        <div
                          key={conv.id}
                          onClick={() => {
                            navigate(`/app/conversations/${conv.id}`)
                            onClose()
                          }}
                          className="flex items-center justify-between p-2.5 rounded-xl border border-slate-100 hover:border-slate-300 hover:bg-slate-50 cursor-pointer transition-all group"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-7 h-7 rounded-lg bg-[#075f55]/10 text-[#075f55] flex items-center justify-center flex-shrink-0">
                              <MessageSquare className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md uppercase ${badgeColor}`}>
                                  {conv.status}
                                </span>
                                <span className="text-[10px] text-gray-400 capitalize">
                                  {conv.mode}
                                </span>
                              </div>
                              <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                                {formatDate(conv.updatedAt)}
                              </p>
                            </div>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-[#075f55] transition-colors flex-shrink-0" />
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">Sin conversaciones registradas</p>
                )}
              </div>

              {/* Timeline */}
              <div className="p-4">
                <button
                  onClick={() => setShowTimeline(!showTimeline)}
                  className="w-full flex items-center justify-between group"
                >
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Actividad Reciente</h4>
                  <span className="text-slate-400 group-hover:text-[#075f55] transition-colors">
                    <ChevronRight className={`w-4 h-4 transition-transform ${showTimeline ? 'rotate-90' : ''}`} />
                  </span>
                </button>

                {showTimeline && (
                  <div className="mt-3 space-y-3">
                    {isLoadingTimeline ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                      </div>
                    ) : timeline && timeline.data.length > 0 ? (
                      <div className="relative space-y-4 before:absolute before:inset-y-0 before:left-[11px] before:w-0.5 before:bg-slate-100">
                      {timeline.data.slice(0, 10).map((event) => (
                        <div key={event.id} className="relative flex items-start gap-4 pl-8">
                          <div className="absolute left-0 top-0 w-6 h-6 bg-[#075f55]/20 text-[#075f55] rounded-full flex items-center justify-center ring-4 ring-white flex-shrink-0">
                            {getTimelineIcon(event.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-700 truncate">
                              {getTimelineLabel(event.type, event.data)}
                            </p>
                            <p className="text-[10px] text-slate-400">
                              {formatDate(event.timestamp)}
                            </p>
                          </div>
                        </div>
                      ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 text-center py-2">No hay eventos</p>
                    )}

                    {timeline && timeline.data.length > 10 && (
                      <button className="w-full text-center text-sm text-[#075f55] hover:text-[#064e46]">
                        Ver timeline completo
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Sticky footer */}
            {isEditing && (
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-100 flex gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.03)]">
                <button
                  onClick={handleSaveEdit}
                  disabled={updateContactMutation.isPending}
                  className="flex-1 px-4 py-2.5 bg-[#075f55] hover:bg-[#064e46] text-white font-bold text-sm rounded-xl transition-all shadow-lg shadow-[#075f55]/20 active:scale-95 disabled:opacity-50"
                >
                  {updateContactMutation.isPending ? 'Guardando...' : 'Guardar cambios'}
                </button>
                <button
                  onClick={() => { setIsEditing(false); setEditedFields({}) }}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm rounded-xl transition-all"
                >
                  Cancelar
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500">Cargando...</p>
          </div>
        )}
      </div>
    </>
  )
}
